import { ErrorHandler, EventEmitter, Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { ErrorReport } from '../error-report';

/**
 * ErrorReporterService is an error handler that surfaces errors visibly on the page, so they can be inspected on any device.
 *
 * To use this in your application, import NomidioDevelopmentModule in your AppModule,
 * and provide this service as your error handler per the example.
 *
 * @example // In app.module.ts
 * { provide: ErrorHandler, useClass: environment.production ? ErrorHandler : ErrorReporterService },
 *
 */
@Injectable()
export class ErrorReporterService extends ErrorHandler {
  /**
   * The entire error report log, past and present, as an observable.
   *
   * This is a replay subject: subscribing to it emits every single log in this object's history in order.
   */
  public readonly reportLog = new ReplaySubject<ErrorReport>();

  /**
   * This event fires each time a log is added.
   *
   * To find error reports you didn't see previously, listen to either `reportLog` or examine `reports`.
   */
  public readonly reportAdded = new EventEmitter<ErrorReport>();

  /**
   * All error reports that have been logged by this service.
   */
  public readonly reports: ErrorReport[] = [];

  /**
   * The latest numeric ID assigned to
   */
  protected latestId = 1;

  public override handleError(error: any): void {
    super.handleError(error);

    try {
      const report = new ErrorReport(error);
      report.ordinal = this.latestId;
      this.latestId++;
      this.reports.push(report);
      this.reportLog.next(report);
      this.reportAdded.emit(report);
    } catch (ex) {
      console.error('Could not log report, original error and exception logging it follow:', error, ex);
    }
  }
}
